import React from 'react';
import { ArrowLeft, CreditCard, Calendar, CheckCircle, XCircle } from 'lucide-react';

const RefundPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Cancellation & Refund Policy</h1>
                <p className="text-gray-600 mt-2">Effective Date: January 1, 2025</p>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              We want you to be completely satisfied with Shortlistly. Here's our clear and fair refund policy.
            </p>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Subscription Cancellation */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <Calendar className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Subscription Cancellation</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">How to Cancel</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white font-bold text-xs">1</span>
                    </div>
                    <p className="text-gray-700">Go to your account dashboard</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white font-bold text-xs">2</span>
                    </div>
                    <p className="text-gray-700">Navigate to subscription settings</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white font-bold text-xs">3</span>
                    </div>
                    <p className="text-gray-700">Click "Cancel Subscription"</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">What Happens Next</h3>
                <div className="bg-green-50 p-4 rounded-xl">
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>You can cancel anytime with no fees</li>
                    <li>Access continues until billing period ends</li>
                    <li>No automatic renewal after cancellation</li>
                    <li>Data remains accessible for 30 days</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Policy */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Refund Eligibility</h2>
            </div>
            
            {/* 7-Day Guarantee */}
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl mb-6">
              <h3 className="font-semibold text-gray-900 text-lg mb-3">7-Day Satisfaction Guarantee</h3>
              <p className="text-gray-700 mb-4">
                We offer a 7-day satisfaction guarantee for first-time users. If you're not completely satisfied, 
                we'll provide a full refund within 7 days of your initial purchase.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Eligible for Full Refund:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li>First-time subscription within 7 days</li>
                    <li>Technical issues preventing service use</li>
                    <li>Service not as described</li>
                    <li>Billing errors or unauthorized charges</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Processing Time:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li>Refund approved within 24-48 hours</li>
                    <li>Credit card refunds: 3-5 business days</li>
                    <li>PayPal refunds: 1-2 business days</li>
                    <li>Bank transfers: 5-7 business days</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Beyond 7 Days */}
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl">
              <h3 className="font-semibold text-gray-900 text-lg mb-3">Beyond 7 Days</h3>
              <p className="text-gray-700 mb-4">
                Refunds beyond 7 days are evaluated on a case-by-case basis and typically only approved for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Confirmed technical issues that prevented service usage</li>
                <li>Billing errors or system malfunctions</li>
                <li>Exceptional circumstances reviewed by our team</li>
              </ul>
            </div>
          </div>

          {/* Non-Refundable Items */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <XCircle className="w-6 h-6 text-red-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Non-Refundable Items</h2>
            </div>
            <div className="bg-red-50 p-6 rounded-xl">
              <p className="text-gray-700 mb-4">The following are not eligible for refunds:</p>
              <div className="grid md:grid-cols-2 gap-6">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Services used beyond the 7-day guarantee period</li>
                  <li>Completed resume optimizations and downloads</li>
                  <li>Change of mind after extensive usage</li>
                </ul>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Violation of terms of service</li>
                  <li>Fraudulent or unauthorized usage</li>
                  <li>Third-party service fees or charges</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How to Request Refund */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">How to Request a Refund</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Contact Our Support Team</h3>
                  <p className="text-gray-600">Email us at <a href="mailto:shortlistly@gmail.com" className="text-blue-600 hover:text-blue-700">shortlistly@gmail.com</a> with your refund request</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Provide Required Information</h3>
                  <p className="text-gray-600">Include your account email, subscription details, and reason for refund</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Review Process</h3>
                  <p className="text-gray-600">We'll review your request within 24 hours and respond with next steps</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Need Help with Refunds?</h2>
            <p className="text-blue-100 mb-6">
              Our support team is here to help with any billing questions or refund requests.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="mailto:shortlistly@gmail.com"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
              >
                Email Support
              </a>
              <span className="text-blue-100 flex items-center">
                Response time: Within 24 hours
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;